import { observable, computed, makeObservable } from 'mobx'
import flatten from 'lodash/flatten'
import { Permission, PermissionSet } from '../dtos/permissions'
import { MemberDTO } from '../dtos/member'
import Workspace from './Workspace'
import { Model } from 'infra/model'
import User from './User'
import { PrimeUser } from 'iam/UserStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { Id } from 'types/definitions'
import { generateAvatarFallback } from 'utils/image-util'
import once from 'lodash/once'
import { PrimeSkill } from 'iam/SkillStore'
import { PrimeRole } from 'iam/RoleStore'
import Role from './Role'
import Skill from './Skill'
import { isNotNil } from 'utils/object-util'
import { Membership } from 'iam/WorkspaceMembershipStore'

/**
 * Query for checking whether a feature is enabled for a permission.
 */
export type IsFeatureEnabledForPermission = (
  permissionId: Permission,
) => boolean

export interface MemberMetadata {
  [id: string]: unknown
}

export default class Member extends Model<MemberDTO, Member> {
  @observable workspace = Workspace.none
  @observable user = User.none
  @observable internalId = Id.none
  @observable givenName = ''
  @observable familyName = ''
  @observable displayName = ''
  @observable email = ''
  @observable picture = ''
  @observable phone = ''
  @observable memberMetadata: MemberMetadata = {}
  @observable dateCreated = DateZero()
  @observable dateModified = DateZero()
  @observable lastSeen: Date | null = DateZero()
  @observable lastLogin: Date | null = DateZero()

  @observable inactive = false
  @observable maxConcurrentProviderJobs = 0
  @observable approvedTermsVersion = ''
  @observable referrer = ''
  @observable memberPermissions: PermissionSet = {}
  @observable computedPermissions: PermissionSet = {}
  @observable notificationPreferences = {}
  @observable roleIds: Array<string> = []
  @observable skillIds: Array<string> = []
  @observable roles: Role[] = []
  @observable skills: Skill[] = []

  /**
   * Default member.
   */
  static get none() {
    return lazyNone()
  }

  constructor(
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeRole: PrimeRole,
    private primeSkill: PrimeSkill,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get isCurrentUser() {
    return this.user.isCurrentUser
  }

  @computed
  get avatar() {
    return this.picture || this.avatarFallback
  }

  @computed
  get avatarFallback() {
    return generateAvatarFallback(
      this.displayName || this.email,
      this.workspace.branding.colors.primary,
    )
  }

  /**
   * Whether the current member represents that of the input
   * IDs.
   */
  is(workspaceId: Id, userId: Id) {
    return this.workspace.id === workspaceId && this.user.id === userId
  }

  hasAllPermissions(...ids: Array<Permission>) {
    ids = flatten(ids)
    if (ids.length === 0) return true
    return ids.every((id) => this.hasPermission(id))
  }

  hasSomePermissions(...ids: Array<Permission>) {
    ids = flatten(ids)
    if (ids.length === 0) return true
    return ids.some((id) => this.hasPermission(id))
  }

  hasPermission(id: Permission) {
    const computedPerms = this.computedPermissions || {}
    return computedPerms[id] === 'allow'
  }

  parse(json: Partial<MemberDTO>): Partial<Member> {
    let user: User | undefined
    if (json.user_public_id || json.user_id) {
      user = this.primeUser({
        user_id: json.user_id,
        email: json.email,
        public_id: json.user_public_id,
        given_name: json.given_name,
        family_name: json.family_name,
        picture: json.picture,
      })

      if (user === User.none) {
        user = undefined
      }
    }

    return {
      user,
      internalId: json.id,
      givenName: json.given_name,
      familyName: json.family_name,
      displayName: json.display_name,
      email: json.email,
      picture: json.picture,
      phone: json.phone,
      inactive: json.inactive,
      memberMetadata: json.member_metadata,
      lastLogin: maybeParseDate(json.last_login),
      lastSeen: maybeParseDate(json.last_seen),
      maxConcurrentProviderJobs: json.max_concurrent_provider_jobs,
      approvedTermsVersion: json.approved_terms_version,
      referrer: json.referrer,
      memberPermissions: json.member_permissions,
      computedPermissions: json.computed_permissions,
      notificationPreferences: json.notification_preferences,
      roles: json.roles
        ? json.roles.map((r) => this.primeRole(r.role)).filter(isNotNil)
        : undefined,
      skills: json.skills
        ? json.skills.map((s) => this.primeSkill(s.skill)).filter(isNotNil)
        : undefined,
      dateCreated: maybeParseDate(json.date_created),
      dateModified: maybeParseDate(json.date_modified),
      workspace: json.workspace_id
        ? this.primeWorkspace({ id: json.workspace_id })
        : undefined,
    }
  }

  toJSON(): MemberDTO {
    return {
      id: this.internalId || '',
      approved_terms_version: this.approvedTermsVersion,
      computed_permissions: this.computedPermissions,
      date_created: this.dateCreated.toISOString(),
      date_modified: this.dateModified.toISOString(),
      display_name: this.displayName,
      email: this.email,
      family_name: this.familyName,
      given_name: this.givenName,
      inactive: this.inactive,
      last_login: this.lastLogin?.toISOString() || null,
      last_seen: this.lastSeen?.toISOString() || null,
      max_concurrent_provider_jobs: this.maxConcurrentProviderJobs,
      member_metadata: this.memberMetadata,
      member_permissions: this.memberPermissions,
      phone: this.phone,
      picture: this.picture,
      referrer: this.referrer,
      roles: [],
      skills: [],
      user_id: this.user.id || '',
      user_public_id: this.user.publicId || '',
      workspace_id: this.workspace.id || '',
      workspace_metadata: {},
    }
  }

  toMembership(): Membership {
    return {
      member: this,
      workspace: this.workspace,
    }
  }
}

const lazyNone = once(() =>
  new Member(
    () => Workspace.none,
    () => User.none,
    () => undefined,
    () => undefined,
  ).set({
    givenName: 'Member',
    familyName: 'Member',
    displayName: 'Member',
  }),
)
